import {
  Avatar,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  StackDivider,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { maybeAddHttps } from "modules/utils";
import { appDisplayName } from "modules/apps/tenants";
import { useMemo, useState } from "react";
import { FiCheck, FiGrid, FiX } from "react-icons/fi";
import { MF_APPS, MFMemberApp } from "state/members/apps";
import MFMembers from "state/members/membersState";
import SearchBox from "ui/SearchBox";

const MyApps = () => {
  const me = MFMembers.useGetMe();
  const apps = MF_APPS.useListApps();
  const [search, setSearch] = useState("");

  const visibleApps = useMemo(
    () =>
      apps.filter(({ app: a }) =>
        a.name?.toLowerCase().includes(search.toLowerCase()) ||
        a.domain?.toLowerCase().includes(search.toLowerCase())
      ),
    [search, apps],
  );
  return (
    <Flex
      w="full"
      h="full"
      px={16}
      pt={8}
      minH="100vh"
      justify="center"
      mb={12}
    >
      <VStack
        w="800px"
        h="full"
        bg="white"
        rounded={8}
        spacing={4}
        p={8}
        border="1px solid"
        borderColor="gray.200"
        shadow="sm"
      >
        <VStack spacing={4} w="full" align="flex-start">
          <HStack justify="space-between" w="full">
            <VStack align="flex-start" spacing={1}>
              <Text
                fontSize="2xl"
                fontWeight="500"
                color="gray.700"

              >
                👋{"      "}Hi {me?.firstName}
              </Text>
              <Text variant={"lil"}>
                Here are all the apps you have access to
              </Text>
            </VStack>
          </HStack>
        </VStack>
        <Divider />
        <SearchBox value={search} onChange={(e) => setSearch(e.target.value)} />
        <VStack align="flex-start" w="full" spacing={0}>
          <HStack w="full" justify="space-between" px={4}>
            <Text variant={"label"}>App</Text>
            <Tooltip label="Help us know whether you still use this app">
              <Text variant={"label"}>Still use?</Text>
            </Tooltip>
          </HStack>
          <VStack
            mt={4}
            w="full"
            rounded={6}
            spacing={0}
            border="1px solid"
            borderColor="gray.200"
            shadow="xs"
            p={0}
            divider={<StackDivider />}
          >
            {visibleApps.map((a) => <AppRow app={a} key={a.id} />)}
          </VStack>
        </VStack>
      </VStack>
    </Flex>
  );
};

const AppRow = ({
  app: mfApp,
}: {
  app: MFMemberApp;
}) => {
  const { app } = mfApp;
  const color = mfApp.requiredStatus === "member_required" ? "green" : "red";

  const required = mfApp.requiredStatus === "member_required"
    ? true
    : undefined;
  const notRequired = mfApp.requiredStatus === "member_not_required"
    ? true
    : undefined;

  const { mutate: changeeStatus } = MF_APPS.useChangeRequiredStatus();

  return (
    <HStack
      w="full"
      px={4}
      py={3}
      justify="space-between"
      _hover={{ bg: "gray.50", cursor: "pointer" }}
      onClick={() => window.open(maybeAddHttps(app.domain), "_blank")}
    >
      <HStack spacing={4}>
        {!app.links.logoUrl
          ? <Icon as={FiGrid} boxSize="24px" />
          : <Avatar src={app.links.logoUrl} size="sm" />}
        <VStack align="flex-start" spacing={"2px"}>
          <HStack>
            <Text fontSize="15px" fontWeight={"500"} color="gray.800">
              {appDisplayName(app)}
            </Text>
            {(mfApp.authAccountDomain ||
              mfApp.authType === "okta") && (
                <Tooltip
                  label={"Signed in via " +
                    (mfApp.authType === "okta"
                      ? "Okta"
                      : mfApp.authAccountDomain)}
                >
                  <Avatar
                    size="2xs"
                    src={"https://ploy-app-logos.s3.eu-west-2.amazonaws.com/" +
                      (mfApp.authType === "okta"
                        ? "okta.com"
                        : mfApp.authAccountDomain)}
                  />
                </Tooltip>
              )}
          </HStack>
          <Text variant="lil">{app.domain}</Text>
        </VStack>
      </HStack>
      <HStack>
        <Tooltip label="I no longer need access to this app">
          <IconButton
            variant={mfApp.requiredStatus === "member_not_required"
              ? "solid"
              : "secondary"}
            icon={<Icon as={FiX} />}
            aria-label="No longer need / no longer use"
            color={notRequired && color + ".700"}
            bg={notRequired && color + ".50"}
            border={notRequired && "1px solid"}
            borderColor={notRequired && color + ".700"}
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              changeeStatus({ status: "member_not_required", appId: app.id });
            }}
          />
        </Tooltip>
        <Tooltip label="I still use this app">
          <IconButton
            variant={mfApp.requiredStatus === "member_required"
              ? "solid"
              : "secondary"}
            icon={<Icon as={FiCheck} />}
            aria-label="I still use this app"
            color={required && color + ".700"}
            bg={required && color + ".50"}
            border={required && "1px solid"}
            borderColor={required && color + ".700"}
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              changeeStatus({ status: "member_required", appId: app.id });
            }}
          />
        </Tooltip>
      </HStack>
    </HStack>
  );
};
export default MyApps;
